import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Building2, MapPin, Star } from "lucide-react";

import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth-client";
import { capture } from "@/lib/posthog";
import { formatMoney } from "@/lib/format";
import { useBetaInformation } from "@/hooks/queries/useBetaInformation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * El aviso es contenido curado, no una vista de datos: la descripción real de
 * SEACE viene en mayúsculas y la entidad con su gerencia entera, así que cada
 * texto se escribe a mano. Cambiar de licitación = editar esta constante.
 */
const FEATURED_PICK = {
  // Destinatarios (`user.id`). Vacío = no se le muestra a nadie.
  userIds: [
    "3LnpWCJm0SZt01vAj9lsmRX6LmVR3ddN",
    "QbHlSHAEz4Ry3SYRbaCJJ34HUCM9NAWx",
    "KLPpQLVDU9kXhxpMHHrucnlAYlKgx84r",
  ],
  contractingId: 171206,
  images: [
    "https://pub-0dca39eb741341ea977662839c163ab9.r2.dev/WhatsApp%20Unknown%202026-09-07%20at%203.36.06%20AM/WhatsApp%20Image%202026-09-07%20at%203.35.55%20AM%20(1).jpeg",
    "https://pub-0dca39eb741341ea977662839c163ab9.r2.dev/WhatsApp%20Unknown%202026-09-07%20at%203.36.06%20AM/WhatsApp%20Image%202026-09-07%20at%203.35.55%20AM%20(2).jpeg",
    "https://pub-0dca39eb741341ea977662839c163ab9.r2.dev/WhatsApp%20Unknown%202026-09-07%20at%203.36.06%20AM/WhatsApp%20Image%202026-09-07%20at%203.35.55%20AM.jpeg",
    "https://pub-0dca39eb741341ea977662839c163ab9.r2.dev/WhatsApp%20Unknown%202026-09-07%20at%203.36.06%20AM/WhatsApp%20Image%202026-09-07%20at%203.35.56%20AM%20(1).jpeg",
    "https://pub-0dca39eb741341ea977662839c163ab9.r2.dev/WhatsApp%20Unknown%202026-09-07%20at%203.36.06%20AM/WhatsApp%20Image%202026-09-07%20at%203.35.56%20AM.jpeg",
  ] as string[],
  rubro: "Asfaltos",
  badge: "Alto potencial de venta",
  kicker: "Monto confirmado en asfalto",
  // TODO: confirmar contra los ítems una vez cargada la relación de precios.
  headline: "S/57 millones en asfalto",
  totalAmount: 782247942,
  title:
    "Mejoramiento de la transitabilidad vial interurbana Piura – Sullana, empalme PE–1N",
  // Fragmento del título que va resaltado; debe aparecer tal cual en `title`.
  titleHighlight: "Piura – Sullana",
  subtitle:
    "Distritos de Miguel Checa, Marcavelica, Ignacio Escudero y Sullana",
  location: "Piura",
  subject: "Obra",
  tag: "Vía de evitamiento",
  entity: "Gobierno Regional de Piura",
};

// La marca lleva el id: si mañana se destaca otra licitación, el aviso vuelve a
// salir sin tener que inventar otra llave.
const SEEN_KEY = `licitigo_featured_pick_${FEATURED_PICK.contractingId}`;

/** Carrusel del encabezado: swipe nativo por scroll-snap y puntos para saltar. */
function ImageCarousel({ images }: { images: string[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  return (
    <>
      <div
        ref={trackRef}
        onScroll={(e) => {
          const track = e.currentTarget;
          setIndex(Math.round(track.scrollLeft / track.clientWidth));
        }}
        className="flex h-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((src) => (
          <img
            key={src}
            src={src}
            alt=""
            className="h-full w-full shrink-0 snap-center object-cover"
          />
        ))}
      </div>

      {images.length > 1 && (
        <div className="absolute top-4 right-5 flex items-center gap-1.5">
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              aria-label={`Foto ${i + 1}`}
              onClick={() =>
                trackRef.current?.scrollTo({
                  left: trackRef.current.clientWidth * i,
                  behavior: "smooth",
                })
              }
              className={cn(
                "h-1.5 cursor-pointer rounded-full bg-white transition-all",
                i === index ? "w-6" : "w-3 opacity-50",
              )}
            />
          ))}
        </div>
      )}
    </>
  );
}

/** Título con un fragmento resaltado en azul. */
function HighlightedTitle({
  title,
  highlight,
}: {
  title: string;
  highlight: string;
}) {
  const start = title.indexOf(highlight);
  if (start < 0) return <>{title}</>;

  return (
    <>
      {title.slice(0, start)}
      <span className="text-primary">{highlight}</span>
      {title.slice(start + highlight.length)}
    </>
  );
}

/**
 * "La mejor licitación de la semana para ti": aviso puntual a un usuario sobre
 * una licitación concreta. Se muestra una vez por navegador y no bloquea nada.
 */
export function FeaturedPickModal() {
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const { data: beta } = useBetaInformation();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(SEEN_KEY) != null,
  );

  // Espera a que el wizard de introducción esté visto: dos modales automáticos
  // encimados dejan al usuario sin saber cuál está respondiendo.
  const open =
    !dismissed &&
    !!session?.user?.id &&
    FEATURED_PICK.userIds.includes(session.user.id) &&
    !!beta?.userextrainformation_introseenat;

  // Eventos propios del aviso (no se reusa `upsell_cta_clicked`: ese viaja a
  // Meta como AddToCart). Con `viewed` como denominador, la decision sale de
  // comparar `reason` en el `closed`.
  useEffect(() => {
    if (open)
      capture("featured_pick_viewed", {
        contractingId: FEATURED_PICK.contractingId,
      });
  }, [open]);

  function close(reason: "dismissed" | "cta") {
    capture("featured_pick_closed", {
      reason,
      contractingId: FEATURED_PICK.contractingId,
    });
    localStorage.setItem(SEEN_KEY, "1");
    setDismissed(true);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close("dismissed")}>
      <DialogContent
        showCloseButton={false}
        className="gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-xl"
      >
        <DialogTitle className="sr-only">{FEATURED_PICK.title}</DialogTitle>
        <DialogDescription className="sr-only">
          {FEATURED_PICK.subtitle}
        </DialogDescription>

        <div className="flex items-center gap-2 bg-primary px-6 py-4 text-primary-foreground">
          <Star className="size-5 fill-current" />
          <span className="text-sm">
            Seleccionado para tu rubro:{" "}
            <strong className="font-bold">{FEATURED_PICK.rubro}</strong>
          </span>
        </div>

        <div className="relative h-56 bg-slate-900 sm:h-64">
          <ImageCarousel images={FEATURED_PICK.images} />

          {/* Degradado propio: sin él el titular se pierde sobre una foto clara. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/20" />

          <span className="absolute top-4 left-5 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3.5 py-1.5 text-sm font-semibold text-white">
            <span className="size-1.5 rounded-full bg-white" />
            {FEATURED_PICK.badge}
          </span>

          <div className="absolute right-5 bottom-4 left-5 text-white">
            <p className="text-xs font-bold tracking-widest text-sky-300 uppercase">
              {FEATURED_PICK.kicker}
            </p>
            <p className="text-3xl font-bold tracking-tight sm:text-4xl">
              {FEATURED_PICK.headline}
            </p>
            <p className="text-sm text-white/80">
              Monto total de la obra · {formatMoney(FEATURED_PICK.totalAmount)}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              <HighlightedTitle
                title={FEATURED_PICK.title}
                highlight={FEATURED_PICK.titleHighlight}
              />
            </h2>
            <p className="mt-1.5 text-sm text-slate-500">
              {FEATURED_PICK.subtitle}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 text-sm font-medium">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-orange-50 px-3 py-1.5 text-orange-700">
              <MapPin className="size-4" />
              {FEATURED_PICK.location}
            </span>
            <span className="rounded-lg bg-emerald-50 px-3 py-1.5 text-emerald-700">
              {FEATURED_PICK.subject}
            </span>
            <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-slate-600">
              {FEATURED_PICK.tag}
            </span>
          </div>

          <div className="flex items-center gap-3 border-t pt-4">
            <span className="flex size-10 items-center justify-center rounded-lg bg-blue-50 text-primary">
              <Building2 className="size-5" />
            </span>
            <div>
              <p className="text-xs font-bold tracking-wider text-slate-400 uppercase">
                Entidad
              </p>
              <p className="font-bold text-slate-900">{FEATURED_PICK.entity}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              close("cta");
              navigate(`/contracting/${FEATURED_PICK.contractingId}`);
            }}
            className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-primary px-6 py-4 font-bold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Ver expediente completo
            <ArrowRight className="size-5" />
          </button>

          <button
            type="button"
            onClick={() => close("dismissed")}
            className="cursor-pointer text-sm font-medium text-slate-500 transition-colors hover:text-slate-800"
          >
            No me interesa
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
